import type { Meal, MealReasonCode } from '@sentra/contracts';
import { MEAL_REASON_CODES } from '@sentra/contracts';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/design/components/dialog';
import { Label } from '@/design/components/label';
import { NativeSelect } from '@/design/components/native-select';

/**
 * Serving a sitting somebody has already had, with the reason that authorises it.
 *
 * **Deliberately NOT `AnomalyDialog`, and the difference is the point.** That one is a full-screen
 * red alert with a siren, and it is that because Section 1.11 requires a band presented by somebody
 * who has been blocked to be impossible to look past. A second helping is a dropped tray. Dressing
 * it in the same clothes would devalue the alert that matters, which is the whole reason this
 * product has exactly one full-screen red.
 *
 * **Mounted per opening rather than toggled.** The reason picker starts empty for every person,
 * which is a property of the component existing rather than of an effect somebody remembered to
 * write — the same arrangement, and the same reasoning, as the anomaly dialog beside it.
 *
 * **There is no note field, because there is no `other`.** All three reasons are self-explanatory
 * and `staff_authorised` is the open one, so nothing in this domain is prose one human wrote about
 * another. That is what keeps `meal_events` free of ciphertext and free of a read-back path.
 *
 * **A refusal is rendered INSIDE the dialog**, for the reason the anomaly dialog renders its own:
 * this is a modal with an overlay, so a banner on the screen underneath is unreachable, and without
 * it pressing the button would appear to do nothing at all.
 */
export function MealRepeatDialog({
  meal,
  servedAtLabel,
  submitting,
  failure,
  onConfirm,
  onCancel,
}: Readonly<{
  meal: Meal;
  /** The already-translated "served at HH:MM" line, so the operator sees what they are repeating. */
  servedAtLabel: string;
  submitting: boolean;
  /** Why the last attempt from inside this dialog was refused, or `null`. */
  failure: string | null;
  onConfirm: (reasonCode: MealReasonCode) => void;
  onCancel: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const reasonId = useId();
  const [reasonCode, setReasonCode] = useState<MealReasonCode | ''>('');

  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        closeLabel={t('common.close')}
        // A stray click outside must not dismiss it, exactly as it must not dismiss an anomaly: a
        // repeat abandoned by accident is one the operator has to discover by pressing again.
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        data-meal-repeat={meal}
      >
        <div className="grid gap-4">
          <DialogTitle className="text-lg font-semibold">{t('scan.meal.repeatTitle')}</DialogTitle>
          <DialogDescription className="text-base text-foreground">
            {t('scan.meal.repeatBody', { meal: t(`scan.meal.${meal}`) })}
          </DialogDescription>
          <p className="text-sm text-muted-foreground">{servedAtLabel}</p>

          <div className="grid gap-1">
            <Label htmlFor={reasonId}>{t('scan.meal.reasonLabel')}</Label>
            <NativeSelect
              id={reasonId}
              data-testid="meal-repeat-reason"
              value={reasonCode}
              aria-describedby={reasonCode === '' ? `${reasonId}-hint` : undefined}
              onChange={(event) => {
                setReasonCode(event.target.value as MealReasonCode | '');
              }}
            >
              <option value="">{t('scan.meal.reasonPlaceholder')}</option>
              {MEAL_REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(`scan.mealReason.${code}`)}
                </option>
              ))}
            </NativeSelect>
            {reasonCode === '' && (
              <p id={`${reasonId}-hint`} className="text-sm text-muted-foreground">
                {t('scan.meal.reasonRequired')}
              </p>
            )}
          </div>

          {failure !== null && (
            <p role="alert" className="rounded-md border border-danger p-3 text-sm text-danger">
              {failure}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              data-testid="meal-repeat-confirm"
              disabled={reasonCode === '' || submitting}
              onClick={() => {
                if (reasonCode !== '') {
                  onConfirm(reasonCode);
                }
              }}
            >
              {t('scan.meal.repeatConfirm')}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="meal-repeat-cancel"
              onClick={onCancel}
              disabled={submitting}
            >
              {t('scan.meal.repeatCancel')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
